import React, { useState, useRef } from "react";
import { t as T } from "i18next";
import { createPortal } from "react-dom";
import { X, Percent, Gift, Type, Sparkles, Image as ImageIcon, Film, QrCode } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const MAX_IMAGES = 3;
const MAX_VIDEO_SEC = 120;

const videoDuration = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); resolve(v.duration); };
  v.onerror = () => resolve(null);
  v.src = URL.createObjectURL(file);
});

function PromoMediaPicker({ mediaFiles, setMediaFiles, qrFile, setQrFile }) {
  const mediaRef = useRef();
  const qrRef = useRef();
  const vids = mediaFiles.filter((f) => f.type.startsWith("video/"));
  const imgs = mediaFiles.filter((f) => f.type.startsWith("image/"));

  const addMedia = async (list) => {
    const next = [...mediaFiles];
    for (const f of Array.from(list || [])) {
      if (f.type.startsWith("video/")) {
        if (next.some((x) => x.type.startsWith("video/"))) { toast.error(T("ui.promo_max_video")); continue; }
        if (f.size > 5 * 1024 * 1024 * 1024) { toast.error(T("ui.promo_video_too_big")); continue; }
        const d = await videoDuration(f);
        if (d && d > MAX_VIDEO_SEC + 0.5) { toast.error(`Filmen er ${Math.round(d)} sek — maks ${MAX_VIDEO_SEC} sek.`); continue; }
        next.push(f);
      } else if (f.type.startsWith("image/")) {
        if (next.filter((x) => x.type.startsWith("image/")).length >= MAX_IMAGES) { toast.error(T("ui.promo_max_images")); continue; }
        if (f.size > 5 * 1024 * 1024 * 1024) { toast.error(T("ui.promo_image_too_big")); continue; }
        next.push(f);
      }
    }
    setMediaFiles(next);
  };

  const label = "font-meta text-[10px] uppercase tracking-wider text-[var(--cream-faint)]";
  return (
    <div className="mb-3 space-y-2">
      <div>
        <span className={label}>{T("ui.promo_media_label")}</span>
        <div className="flex flex-wrap gap-2 mt-1">
          {mediaFiles.map((f, i) => (
            <div key={i} className="relative w-16 h-16 bg-black/40 rounded overflow-hidden" data-testid={`promo-media-thumb-${i}`}>
              {f.type.startsWith("video/")
                ? <div className="flex items-center justify-center h-full"><Film className="h-6 w-6 text-[var(--cream-soft)]" /></div>
                : <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />}
              <button onClick={() => setMediaFiles(mediaFiles.filter((_, j) => j !== i))}
                className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5" data-testid={`promo-media-remove-${i}`}>
                <X className="h-3 w-3 text-white" />
              </button>
            </div>
          ))}
          {(vids.length < 1 || imgs.length < MAX_IMAGES) && (
            <button type="button" onClick={() => mediaRef.current?.click()}
              className="w-16 h-16 border border-dashed border-white/20 rounded flex flex-col items-center justify-center text-[var(--cream-soft)] hover:border-[var(--magenta)]"
              data-testid="promo-media-add">
              <ImageIcon className="h-4 w-4" /><span className="text-[9px] mt-0.5">{T("ui.add")}</span>
            </button>
          )}
        </div>
        <input ref={mediaRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
          multiple className="hidden" onChange={(e) => { addMedia(e.target.files); e.target.value = ""; }}
          data-testid="promo-media-input" />
      </div>
      <div>
        <span className={label}>{T("ui.promo_qr_label")}</span>
        <div className="flex items-center gap-2 mt-1">
          {qrFile ? (
            <div className="relative w-16 h-16 bg-white rounded overflow-hidden" data-testid="promo-qr-thumb">
              <img src={URL.createObjectURL(qrFile)} alt="QR" className="w-full h-full object-contain" />
              <button onClick={() => setQrFile(null)} className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5" data-testid="promo-qr-remove">
                <X className="h-3 w-3 text-white" />
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => qrRef.current?.click()}
              className="inline-flex items-center gap-1 text-xs px-2 py-1.5 border border-white/10 rounded text-[var(--cream-soft)] hover:border-[var(--magenta)]"
              data-testid="promo-qr-add">
              <QrCode className="h-4 w-4" /> {T("ui.upload_qr")}
            </button>
          )}
        </div>
        <input ref={qrRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f && f.size > 5 * 1024 * 1024) toast.error(T("ui.qr_too_big"));
            else if (f) setQrFile(f);
            e.target.value = "";
          }}
          data-testid="promo-qr-input" />
      </div>
    </div>
  );
}

const PROMO_KINDS = [
  { kind: "percent",  label: "Rabatt %",  icon: Percent, hint: "Prosent-rabatt med valgfri lenke og utløp" },
  { kind: "freelink", label: "Freelink",  icon: Gift,    hint: "Gratis-tilgang via lenke (f.eks. OnlyFans-link)" },
  { kind: "text",     label: "Tekst",     icon: Type,    hint: "Fri tekst-tilbud uten pris/prosent" },
];

/**
 * PromoModal — lager et tilbud som bakes inn i en wall-post.
 * Tre varianter: percent, freelink, text. Returnerer {kind, title, body, link, discount_pct, expires_at}.
 */
export default function PromoModal({ open, onClose, onApply, initial = null }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState(initial?.kind || "percent");
  const [title, setTitle] = useState(initial?.title || "");
  const [body, setBody] = useState(initial?.body || "");
  const [link, setLink] = useState(initial?.link || "");
  const [pct, setPct] = useState(initial?.discount_pct || 20);
  const [hasExpiry, setHasExpiry] = useState(!!initial?.expires_at);
  const [expiresAt, setExpiresAt] = useState(initial?.expires_at || "");
  const [mediaFiles, setMediaFiles] = useState(initial?.mediaFiles || []);
  const [qrFile, setQrFile] = useState(initial?.qrFile || null);

  if (!open) return null;

  const canApply = (() => {
    if (!title.trim()) return false;
    if (kind === "percent" && (!pct || pct < 1 || pct > 99)) return false;
    if (kind === "freelink" && !link.trim() && !qrFile) return false;
    return true;
  })();

  const apply = () => {
    const data = {
      kind,
      title: title.trim(),
      body: body.trim(),
      link: link.trim() || null,
      mediaFiles,
      qrFile,
    };
    if (kind === "percent") data.discount_pct = parseInt(pct, 10);
    if (hasExpiry && expiresAt) data.expires_at = expiresAt;
    onApply(data);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="promo-modal"
    >
      <div
        className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-[#141116] border border-white/10 rounded-md shadow-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-fraunces italic text-xl text-[var(--cream)] inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[var(--magenta)]" /> Lag tilbud
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/5 text-[var(--cream-faint)]"
            aria-label={T("ui.close")}
            data-testid="promo-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Type-velger */}
        <div className="grid grid-cols-3 gap-1.5 mb-3">
          {PROMO_KINDS.map((k) => {
            const Icon = k.icon;
            const active = kind === k.kind;
            return (
              <button
                key={k.kind}
                onClick={() => setKind(k.kind)}
                className={`flex flex-col items-center gap-1 p-2 rounded-md border transition ${
                  active
                    ? "border-[var(--magenta)] bg-[var(--magenta)]/10 text-[var(--magenta)]"
                    : "border-white/10 text-[var(--cream-soft)] hover:border-white/20"
                }`}
                data-testid={`promo-kind-${k.kind}`}
              >
                <Icon className="h-4 w-4" />
                <span className="text-xs font-meta">{k.label}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-[var(--cream-faint)] font-manrope mb-3">
          {PROMO_KINDS.find((k) => k.kind === kind)?.hint}
        </p>

        {/* Felles felter */}
        <label className="block mb-2">
          <span className="font-meta text-[10px] uppercase tracking-wider text-[var(--cream-faint)]">{T("ui.title")}</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder={T("ui.promo_title_ph")}
            className="input-editorial w-full mt-0.5 px-2 py-1.5 text-sm"
            data-testid="promo-title"
          />
        </label>

        {kind === "percent" && (
          <label className="block mb-2">
            <span className="font-meta text-[10px] uppercase tracking-wider text-[var(--cream-faint)]">{T("ui.discount_pct")}</span>
            <div className="flex items-center gap-2 mt-0.5">
              <input
                type="number" min={1} max={99}
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                className="input-editorial w-20 px-2 py-1.5 text-sm"
                data-testid="promo-pct"
              />
              <span className="text-[var(--magenta)] font-bold text-lg">%</span>
            </div>
          </label>
        )}

        <label className="block mb-2">
          <span className="font-meta text-[10px] uppercase tracking-wider text-[var(--cream-faint)]">
            {T("ui.link")} {kind === "freelink" && T("ui.link_required_no_qr")}
          </span>
          <input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            maxLength={500}
            placeholder="https://..."
            className="input-editorial w-full mt-0.5 px-2 py-1.5 text-sm"
            data-testid="promo-link"
          />
        </label>

        <label className="block mb-3">
          <span className="font-meta text-[10px] uppercase tracking-wider text-[var(--cream-faint)]">{T("ui.desc_optional")}</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder={T("ui.promo_desc_ph")}
            className="input-editorial w-full mt-0.5 px-2 py-1.5 text-sm resize-none"
            data-testid="promo-body"
          />
        </label>

        <PromoMediaPicker
          mediaFiles={mediaFiles}
          setMediaFiles={setMediaFiles}
          qrFile={qrFile}
          setQrFile={setQrFile}
        />

        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={hasExpiry}
            onChange={(e) => setHasExpiry(e.target.checked)}
            data-testid="promo-has-expiry"
          />
          <span className="text-xs text-[var(--cream-soft)]">{T("ui.set_expiry")}</span>
        </label>
        {hasExpiry && (
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="input-editorial w-full mb-3 px-2 py-1.5 text-sm"
            data-testid="promo-expires"
          />
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
          {initial && (
            <button
              onClick={() => onApply(null)}
              className="btn-ghost text-xs px-3 py-1.5 text-red-400"
              data-testid="promo-remove"
            >
              Fjern tilbud
            </button>
          )}
          <button
            onClick={onClose}
            className="btn-ghost text-xs px-3 py-1.5"
            data-testid="promo-cancel"
          >
            {t("common.cancel", "Avbryt")}
          </button>
          <button
            onClick={apply}
            disabled={!canApply}
            className="c-btn c-btn-primary text-sm px-4 py-1.5"
            data-testid="promo-apply"
          >
            {T("ui.add_offer")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
